/**
 * WorkshopStep — the Create Workshop wizard step for type, capacity and ticket price.
 *
 * Extracted from workshopCreateSteps so each file declares one component
 * (`react/no-multi-comp`); behaviour is unchanged.
 */
import { TWO_COLUMN_GRID } from '../../primitives/two-column-grid';
import { Field, fieldStyle, MONO } from '../../venues/venueCreateFields';
import { WorkshopTypeCombobox } from '../detail/WorkshopTypeCombobox';

import type { WorkshopCreateForm } from './useWorkshopCreateForm';
import type { WorkshopTypeOption } from './workshopCreateSteps';

const COL: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 14 };

/** Leading "$" adornment for the ticket-price input (mirrors the detail-page
 *  PriceInput). Matches the field height/border so the two read as one control. */
function pricePrefixStyle(error: boolean): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    height: 32,
    padding: '0 9px',
    fontSize: 12.5,
    color: 'var(--nb-subtle)',
    background: 'var(--nb-bg-2, var(--nb-bg))',
    border: `1px solid ${error ? '#c0392b' : 'var(--nb-border-2)'}`,
    borderRight: 'none',
    borderRadius: '8px 0 0 8px',
  };
}

export function WorkshopStep({
  form,
  types,
}: {
  form: WorkshopCreateForm;
  types: WorkshopTypeOption[];
}) {
  const { draft, fieldError, applyTypeDefaults, editWorkshop } = form;
  const onPickType = (id: string) => {
    const t = types.find((o) => o.id === id);
    applyTypeDefaults(id, {
      capacity: t?.defaultCapacity,
      priceCents: t?.defaultPriceCents,
      durationMinutes: t?.defaultDurationMinutes,
    });
  };
  return (
    <div style={COL}>
      <Field label="Workshop type" required error={fieldError('workshopTypeId')}>
        <WorkshopTypeCombobox
          value={draft.workshop.workshopTypeId}
          options={types.map((t) => ({ id: t.id, label: t.label }))}
          onChange={onPickType}
          error={!!fieldError('workshopTypeId')}
        />
      </Field>
      <div style={TWO_COLUMN_GRID}>
        <Field label="Capacity" required error={fieldError('capacity')}>
          <input
            type="number"
            min={0}
            aria-label="Capacity"
            style={fieldStyle(fieldError('capacity') ? 'error' : undefined, { fontFamily: MONO })}
            value={draft.workshop.capacity}
            onChange={(e) => editWorkshop('capacity', e.target.value)}
          />
        </Field>
        <Field label="Ticket price" required error={fieldError('priceCents')}>
          {/* Static "$" prefix + bare-number input — the value never stores the
              "$". No placeholder: a hardcoded "$65" placeholder used to make a
              no-default type look pre-filled (BON-218). */}
          <div style={{ display: 'flex', alignItems: 'stretch' }}>
            <span aria-hidden="true" style={pricePrefixStyle(!!fieldError('priceCents'))}>
              $
            </span>
            <input
              type="number"
              min={0}
              aria-label="Ticket price"
              style={fieldStyle(fieldError('priceCents') ? 'error' : undefined, {
                fontFamily: MONO,
                borderRadius: '0 8px 8px 0',
              })}
              value={draft.workshop.price}
              onChange={(e) => editWorkshop('price', e.target.value)}
            />
          </div>
        </Field>
      </div>
      <p style={{ fontSize: 10.5, color: 'var(--nb-subtle)', lineHeight: 1.5, margin: 0 }}>
        Add the teacher, assistants & trainees from the workshop page after creating.
      </p>
    </div>
  );
}
